import { useState, useEffect } from 'react';
import { api, extractErrorMessage } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../components/Toast';
import { Button } from '../../components/Button';
import { Badge } from '../../components/Badge';
import { HelpTip } from '../../components/HelpTip';
import type { ConfigPrecios, TarifaEnvioMl } from './types';
import { ComisionesMlPanel } from './ComisionesMlPanel';
import { CostosMlPanel } from './CostosMlPanel';

/** '%' = porcentaje de 0 a 100; '$' = pesos enteros de 1 a 1000. */
const CAMPOS_CONFIG_PRECIOS: Array<{ key: keyof Omit<ConfigPrecios, 'actualizadoPor' | 'updatedAt' | 'cantidadesMayoristas'>; label: string; unidad: '%' | '$' }> = [
  { key: 'comisionClasicaPct', label: 'Comisión Clásica (gold_special)', unidad: '%' },
  { key: 'comisionPremiumPct', label: 'Comisión Premium (gold_pro)', unidad: '%' },
  { key: 'comisionPremiumProPct', label: 'Comisión Premium Pro', unidad: '%' },
  { key: 'costoFijoPct', label: 'Costo fijo', unidad: '%' },
  { key: 'iibbSirtacPct', label: 'IIBB SIRTAC', unidad: '%' },
  { key: 'iibbTucumanPct', label: 'IIBB Tucumán', unidad: '%' },
  { key: 'iibbProvincialPct', label: 'IIBB provincial', unidad: '%' },
  { key: 'redondeoPrecio', label: 'Redondear el precio a múltiplos de', unidad: '$' },
  { key: 'umbralAlertaPct', label: 'Alerta si el precio de ML se aleja del sugerido más de', unidad: '%' },
];

/** "2, 5, 10" → [2, 5, 10]; null si no son hasta 5 cantidades enteras de 2 o más (vacío = ninguna). */
function cantidadesMayoristas(texto: string): number[] | null {
  const partes = texto.split(',').map(s => s.trim()).filter(Boolean);
  const nums = partes.map(Number);
  if (nums.length > 5 || nums.some(n => !Number.isInteger(n) || n < 2 || n > 1000)) return null;
  return [...new Set(nums)].sort((a, b) => a - b);
}

function valorInvalido(unidad: '%' | '$', v: number): string | null {
  if (unidad === '$') return Number.isInteger(v) && v >= 1 && v <= 1000 ? null : 'tiene que ser un número entero de pesos, de 1 a 1000';
  return Number.isFinite(v) && v >= 0 && v <= 100 ? null : 'tiene que ser un número entre 0 y 100';
}

export function ParametrosPrecios() {
  const { user } = useAuth();
  const toast = useToast();
  const esAdmin = user?.role === 'admin';
  const [config, setConfig] = useState<ConfigPrecios | null>(null);
  const [borrador, setBorrador] = useState<Record<string, string>>({});
  const [tarifas, setTarifas] = useState<TarifaEnvioMl[]>([]);
  const [costos, setCostos] = useState<Record<number, string>>({});
  const [guardando, setGuardando] = useState<'config' | 'tarifas' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const cargarConfig = (c: ConfigPrecios) => {
    setConfig(c);
    setBorrador({
      ...Object.fromEntries(CAMPOS_CONFIG_PRECIOS.map(f => [f.key, String(c[f.key])])),
      cantidadesMayoristas: (c.cantidadesMayoristas ?? []).join(', '),
    });
  };
  const cargarTarifas = (t: TarifaEnvioMl[]) => {
    setTarifas(t);
    setCostos(Object.fromEntries(t.map(x => [x.id, x.costo == null ? '' : String(x.costo)])));
  };

  useEffect(() => {
    Promise.all([
      api.get<ConfigPrecios>('/api/v3/precios/configuracion'),
      api.get<TarifaEnvioMl[]>('/api/v3/precios/tarifas-envio'),
    ])
      .then(([c, t]) => { cargarConfig(c); cargarTarifas(t); })
      .catch(e => setError(extractErrorMessage(e)));
  }, []);

  const numero = (s: string) => Number(s.trim().replace(',', '.'));

  const guardarConfig = async () => {
    const cambios: Record<string, number | number[]> = {};
    for (const f of CAMPOS_CONFIG_PRECIOS) {
      const texto = (borrador[f.key] ?? '').trim();
      const v = numero(texto);
      const problema = texto ? valorInvalido(f.unidad, v) : 'no puede quedar vacío';
      if (problema) {
        toast.addToast(`${f.label}: ${problema}`, 'error');
        return;
      }
      cambios[f.key] = v;
    }
    const cantidades = cantidadesMayoristas(borrador.cantidadesMayoristas ?? '');
    if (!cantidades) {
      toast.addToast('Cantidades para precios mayoristas: tienen que ser hasta 5 cantidades enteras de 2 o más, separadas por coma', 'error');
      return;
    }
    cambios.cantidadesMayoristas = cantidades;
    setGuardando('config');
    try {
      cargarConfig(await api.put<ConfigPrecios>('/api/v3/precios/configuracion', cambios));
      toast.addToast('Parámetros guardados', 'success');
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setGuardando(null);
    }
  };

  const guardarTarifas = async () => {
    const cambios: Array<{ id: number; costo: number | null }> = [];
    for (const t of tarifas) {
      const texto = (costos[t.id] ?? '').trim();
      const costo = texto === '' ? null : numero(texto);
      if (costo !== null && (!Number.isFinite(costo) || costo < 0)) {
        toast.addToast(`Tarifa "${t.rango}": costo inválido`, 'error');
        return;
      }
      if (costo !== t.costo) cambios.push({ id: t.id, costo });
    }
    if (cambios.length === 0) { toast.addToast('No hay cambios en las tarifas', 'info'); return; }
    setGuardando('tarifas');
    try {
      cargarTarifas(await api.put<TarifaEnvioMl[]>('/api/v3/precios/tarifas-envio', { tarifas: cambios }));
      toast.addToast(`${cambios.length} tarifa(s) guardada(s)`, 'success');
    } catch (e) {
      toast.addToast(extractErrorMessage(e), 'error');
    } finally {
      setGuardando(null);
    }
  };

  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!config) return <p className="text-sm text-slate-400">Cargando…</p>;

  return (
    <div className="space-y-5">
      {!esAdmin && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm text-slate-600">Sólo un administrador puede cambiar los parámetros.</div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-4 flex items-center gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Parámetros globales</h2>
          <HelpTip tips={[
            'Se aplican a todas las publicaciones de la solapa "Por publicación".',
            'IIBB provincial: el promedio de Ingresos Brutos de todas las provincias; cambia cada año. El precio sugerido lo cubre y se muestra aparte.',
            'Redondeo: el precio sugerido se redondea siempre para arriba a este múltiplo de pesos (por ejemplo, de a $5).',
            'Precios mayoristas: hasta 5 cantidades separadas por coma (por ejemplo 2, 5, 10, 15, 20). Para cada una se calcula el precio por unidad comprando desde esa cantidad. Vacío: sin precios mayoristas.',
            'Alerta: en «Por publicación» se marca la publicación cuyo precio en Mercado Libre se aleja del sugerido más que este porcentaje (para arriba o para abajo). Con 0 no hay alertas.',
            'Estas comisiones son la tabla de respaldo: si hay un dato real de ML vigente (ver "Comisiones de ML"), se usa ése.',
            'La comisión de la tabla sale del tipo de publicación de ML: gold_special = Clásica, gold_pro = Premium. Un tipo desconocido usa Premium y se avisa.',
          ]} />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {CAMPOS_CONFIG_PRECIOS.map(f => (
            <div key={f.key} className="flex flex-col gap-1">
              <label htmlFor={`cfg-${f.key}`} className="text-xs font-medium uppercase tracking-wide text-slate-500">{f.label}</label>
              <div className="flex items-center gap-1 rounded border border-slate-200 bg-white px-3 py-2">
                {f.unidad === '$' && <span className="select-none text-sm text-slate-400">$</span>}
                <input id={`cfg-${f.key}`} value={borrador[f.key] ?? ''} disabled={!esAdmin} inputMode="decimal"
                  onChange={e => setBorrador({ ...borrador, [f.key]: e.target.value })}
                  className="min-w-0 flex-1 bg-transparent font-mono text-sm text-slate-800 focus:outline-none disabled:text-slate-500" />
                {f.unidad === '%' && <span className="select-none text-sm text-slate-400">%</span>}
              </div>
            </div>
          ))}
          <div className="flex flex-col gap-1">
            <label htmlFor="cfg-cantidadesMayoristas" className="text-xs font-medium uppercase tracking-wide text-slate-500">Cantidades para precios mayoristas</label>
            <div className="flex items-center gap-1 rounded border border-slate-200 bg-white px-3 py-2">
              <input id="cfg-cantidadesMayoristas" value={borrador.cantidadesMayoristas ?? ''} disabled={!esAdmin} placeholder="sin precios mayoristas"
                onChange={e => setBorrador({ ...borrador, cantidadesMayoristas: e.target.value })}
                className="min-w-0 flex-1 bg-transparent font-mono text-sm text-slate-800 focus:outline-none disabled:text-slate-500" />
              <span className="select-none text-sm text-slate-400">unidades</span>
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-slate-400">
            {config.actualizadoPor
              ? `Último cambio: ${config.actualizadoPor}${config.updatedAt ? `, ${new Date(config.updatedAt).toLocaleString('es-AR')}` : ''}`
              : 'Valores por defecto'}
          </span>
          {esAdmin && <Button onClick={guardarConfig} loading={guardando === 'config'}>Guardar parámetros</Button>}
        </div>
      </div>

      <ComisionesMlPanel esAdmin={esAdmin} />

      <CostosMlPanel />

      <div className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-4 flex items-center gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Tarifas de envío por rango de peso</h2>
          <HelpTip tips={[
            'Los rangos salen de la columna "PESO ML" de 02_CODIGOS; el sync da de alta los rangos nuevos solo.',
            'Vacío = sin tarifa: el precio sugerido usa envío $0 y muestra el aviso "sin tarifa de envío".',
          ]} />
        </div>
        {tarifas.length === 0 ? (
          <p className="text-sm text-slate-400">Todavía no hay rangos: aparecen después de sincronizar 02_CODIGOS con la columna "PESO ML".</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-3 py-2">Rango</th>
                  <th className="px-3 py-2 text-right">Hasta (kg)</th>
                  <th className="px-3 py-2 text-right">Publicaciones</th>
                  <th className="px-3 py-2 text-right">Costo</th>
                </tr>
              </thead>
              <tbody>
                {tarifas.map(t => (
                  <tr key={t.id} className="border-t border-slate-100">
                    <td className="px-3 py-2">{t.rango}{t.costo == null && <Badge tone="amber" className="ml-2">sin tarifa</Badge>}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{t.pesoMaxKg ?? '—'}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{t.publicaciones}</td>
                    <td className="px-3 py-2 text-right">
                      <input aria-label={`Costo de envío ${t.rango}`} value={costos[t.id] ?? ''} disabled={!esAdmin} inputMode="decimal" placeholder="sin tarifa"
                        onChange={e => setCostos({ ...costos, [t.id]: e.target.value })}
                        className="w-32 rounded border border-slate-200 bg-white px-2 py-1 text-right font-mono text-sm focus:border-brand-400 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {esAdmin && tarifas.length > 0 && (
          <div className="mt-4 flex justify-end">
            <Button onClick={guardarTarifas} loading={guardando === 'tarifas'}>Guardar tarifas</Button>
          </div>
        )}
      </div>
    </div>
  );
}
